import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  Chip,
  Container,
  Divider,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Page from 'src/components/Page';
import Support from 'src/api/support';

// null = all statuses
const STATUS_TABS = [
  { value: 'open', label: 'Open' },
  { value: 'awaiting_user', label: 'Awaiting User' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' }
];

const STATUS_LABELS = {
  open: 'Open',
  awaiting_user: 'Awaiting User',
  resolved: 'Resolved'
};

const STATUS_COLORS = {
  open: 'secondary',
  awaiting_user: 'default',
  resolved: 'primary'
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  row: {
    cursor: 'pointer'
  }
}));

const formatDate = value => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const SupportInboxView = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [status, setStatus] = useState('open');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Support.getTickets(status === 'all' ? undefined : status)
      .then(res => {
        if (active) setTickets(res.data.data || []);
      })
      .catch(() => {
        if (active) setTickets([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [status]);

  // Near-real-time silent auto-refresh of the list every 15s, respecting the
  // active status tab. `status` is in the deps so the interval is re-created
  // (and stale closures avoided) when the tab changes. The poll does NOT toggle
  // the loading spinner — it only updates the list state, so ordering by
  // lastMessageAt stays fresh without a spinner flicker.
  useEffect(() => {
    const POLL_MS = 15000;
    let cancelled = false;

    const poll = () => {
      if (document.hidden) return;
      Support.getTickets(status === 'all' ? undefined : status)
        .then(res => {
          if (!cancelled) setTickets(res.data.data || []);
        })
        .catch(() => {
          /* swallow background-poll errors; the foreground load owns errors */
        });
    };

    const intervalId = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [status]);

  return (
    <Page className={classes.root} title="Support Tickets">
      <Container maxWidth={false}>
        <Card>
          <CardHeader
            title="Support Tickets"
            subheader="Tickets escalated from the AI chatbot"
          />
          <Tabs
            value={status}
            onChange={(e, value) => setStatus(value)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
          >
            {STATUS_TABS.map(t => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>
          <Divider />
          <PerfectScrollbar>
            <Box minWidth={800}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Requester</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Last activity</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.map(t => (
                    <TableRow
                      hover
                      key={t.id}
                      className={classes.row}
                      onClick={() => navigate(`/app/support/tickets/${t.id}`)}
                    >
                      <TableCell>{t.reference}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{t.name || '—'}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{t.subject || '—'}</TableCell>
                      <TableCell>{t.source}</TableCell>
                      <TableCell>{formatDate(t.lastMessageAt)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={STATUS_COLORS[t.status] || 'default'}
                          label={STATUS_LABELS[t.status] || t.status}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && tickets.length === 0 && (
                <Box p={3}>
                  <Typography align="center" color="textSecondary">
                    No tickets in this view.
                  </Typography>
                </Box>
              )}
            </Box>
          </PerfectScrollbar>
        </Card>
      </Container>
    </Page>
  );
};

export default SupportInboxView;
